import { useState, useEffect } from 'react';
import { Info, Shield } from 'lucide-react';
import { useConfigTab, FORM_STYLES, Toggle, ConfigTimestamps, LoadingState } from './shared';

export function GeneralTab({ merchantId, reloadKey }) {
  const { loading, saving, isNew, configValue, metadata, saveConfig } = useConfigTab(
    merchantId, 'MERCHANT_GENERAL_CONFIGURATIONS', reloadKey,
  );

  const [isPickupAddressShownOnLabel, setIsPickupAddressShownOnLabel] = useState(true);
  const [isDropCustomerDetailsFetchingEnabledByPhone, setIsDropCustomerDetailsFetchingEnabledByPhone] = useState(false);
  const [isCreateOrderConfirmationCheckEnabled, setIsCreateOrderConfirmationCheckEnabled] = useState(false);
  const [isCreateOrderFulfillmentConfirmationRequestForChannelOrderEnabled, setIsCreateOrderFulfillmentConfirmationRequestForChannelOrderEnabled] = useState(false);

  useEffect(() => {
    if (!configValue) return;
    setIsPickupAddressShownOnLabel(configValue.is_pickup_address_shown_on_label !== false);
    setIsDropCustomerDetailsFetchingEnabledByPhone(configValue.is_drop_customer_details_fetching_enabled_by_phone === true);
    setIsCreateOrderConfirmationCheckEnabled(configValue.is_create_order_confirmation_check_enabled === true);
    setIsCreateOrderFulfillmentConfirmationRequestForChannelOrderEnabled(
      configValue.is_create_order_fulfillment_confirmation_request_for_channel_order_enabled === true,
    );
  }, [configValue]);

  const handleSave = () => {
    const payload = {
      is_pickup_address_shown_on_label: isPickupAddressShownOnLabel,
      is_drop_customer_details_fetching_enabled_by_phone: isDropCustomerDetailsFetchingEnabledByPhone,
      is_create_order_confirmation_check_enabled: isCreateOrderConfirmationCheckEnabled,
      is_create_order_fulfillment_confirmation_request_for_channel_order_enabled:
        isCreateOrderFulfillmentConfirmationRequestForChannelOrderEnabled,
    };

    if (configValue) {
      payload.client_logo_path = configValue.client_logo_path;
      payload.gstin = configValue.gstin;
      payload.parent_client_id = configValue.parent_client_id;
    }

    saveConfig({ merchant_general_configurations_value: payload });
  };

  if (loading) return <LoadingState />;

  return (
    <div className="space-y-4">
      {isNew && (
        <div className="flex items-center gap-2 p-2.5 bg-amber-50 border border-amber-200 rounded-lg text-xs sm:text-sm text-amber-800">
          <span className="font-medium">No existing configuration</span> - fill the form below and save to create.
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-4">
        <div className={FORM_STYLES.sectionCard}>
          <h3 className={FORM_STYLES.sectionTitle}>
            <Shield className="w-3.5 h-3.5 text-slate-400" />
            Account Info
          </h3>
          <div>
            <label className={FORM_STYLES.label}>Merchant ID</label>
            <input
              type="text"
              value={merchantId || ''}
              readOnly
              disabled
              className={FORM_STYLES.inputDisabled + ' font-mono text-sm'}
            />
            <p className="text-[11px] text-slate-400 mt-1">Your unique merchant identifier.</p>
          </div>
        </div>

        <div className={FORM_STYLES.sectionCard}>
          <h3 className={FORM_STYLES.sectionTitle}>Shipment & Address Flags</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Toggle
              id="pickup-address-label"
              checked={isPickupAddressShownOnLabel}
              onChange={setIsPickupAddressShownOnLabel}
              label="Show pickup address on label"
              description="Display the pickup address on shipment labels"
            />
            <Toggle
              id="drop-customer-details-phone"
              checked={isDropCustomerDetailsFetchingEnabledByPhone}
              onChange={setIsDropCustomerDetailsFetchingEnabledByPhone}
              label="Fetch drop customer details by phone"
              description="Enable fetching drop customer details using phone number"
            />
          </div>
        </div>
      </div>

      <div className={FORM_STYLES.sectionCard}>
        <h3 className={FORM_STYLES.sectionTitle}>Order Confirmation Flags</h3>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="space-y-2">
            <div className="flex items-start gap-2 px-2.5 py-2 bg-blue-50 border border-blue-100 rounded-lg text-[11px] text-blue-700 leading-relaxed">
              <Info className="w-3 h-3 shrink-0 mt-0.5" />
              <span>Enable to send order confirmation on WhatsApp for manually placed orders.</span>
            </div>
            <Toggle
              id="create-order-confirmation-check"
              checked={isCreateOrderConfirmationCheckEnabled}
              onChange={setIsCreateOrderConfirmationCheckEnabled}
              label="Order confirmation for manual orders"
              description="Send WhatsApp confirmation for manually placed orders"
            />
          </div>

          <div className="space-y-2">
            <div className="flex items-start gap-2 px-2.5 py-2 bg-blue-50 border border-blue-100 rounded-lg text-[11px] text-blue-700 leading-relaxed">
              <Info className="w-3 h-3 shrink-0 mt-0.5" />
              <span>Enable to send order confirmation on WhatsApp for Shopify and other channel orders.</span>
            </div>
            <Toggle
              id="fulfillment-confirmation-channel-order"
              checked={isCreateOrderFulfillmentConfirmationRequestForChannelOrderEnabled}
              onChange={setIsCreateOrderFulfillmentConfirmationRequestForChannelOrderEnabled}
              label="Fulfillment confirmation for channel orders"
              description="Send WhatsApp confirmation for Shopify and channel orders"
            />
          </div>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-3 border-t border-slate-100">
        <button type="button" onClick={handleSave} disabled={saving} className={FORM_STYLES.btnPrimary}>
          {saving
            ? <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            : <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
          }
          {saving ? 'Saving…' : 'Save Configuration'}
        </button>
        {!isNew && <ConfigTimestamps metadata={metadata} />}
      </div>
    </div>
  );
}
